import { opisiGresku } from './greska';
import {
  AddressQueueSummary,
  AmendmentPlan,
  AnalyticsReport,
  ReservationLeg,
  ParkedGoodsView,
  CustomerPickupResult,
  DeliveredDay,
  AddressSuggestionResult,
  AwaitingItem,
  Corridor,
  Delivery,
  Driver,
  DriverLink,
  ExceptionDecisionOption,
  FillEstimate,
  GeocodeRun,
  ChannelHealth,
  PozadinskiPosao,
  RezervacijaRadnja,
  RezervacijePregled,
  ClosedInErpOnTour,
  DeliveryOnRemovedStop,
  GoodsInVehicle,
  NetworkLocation,
  ConflictResolution,
  ObstacleGroup,
  SklonjenaIsporuka,
  RosterPlan,
  PendingLocation,
  OpenException,
  PackingList,
  PendingAddress,
  RejectedAddress,
  ErpWriteNeedingAttention,
  OnlineNeodlucena,
  PostingToDo,
  Reconciliation,
  RoadRoute,
  RouteTemplate,
  SavedTour,
  SignedInUser,
  Settings,
  TourPreview,
  TourDetail,
  TourProgress,
  UnresolvedDocument,
  TourProposal,
  Vehicle,
  OutboxMessage,
  MessagingAccount,
  StoreContact,
  VehiclePositions,
  VehicleTrace,
  LocalDelivery,
  StoreCrew,
  StoreTour,
  ErpCapabilities,
  MeasurementSeedResult,
  LeadTimeReport,
  VehicleDayReport,
  CustomerRatingReport,
} from '../types';

import { API_BASE as BASE } from './apiBase';

/**
 * Čitanje sa servera.
 *
 * `cache: 'no-store'` — NE gleda u keš i NE upisuje u njega.
 *
 * Server od 08.10.2026. i sam zabranjuje keširanje API odgovora (`ApiBezKesa`),
 * ali to važi za odgovore koji OD SAD stižu. Zapisi koji su već u pregledaču se
 * time ne diraju — a oni su bili pokvareni: dok je trajao kvar sa deljenjem
 * posla, odgovori su se presecali u pola i takvi završili u disk kešu. Tri dela
 * ekrana „Sa terena" su zbog toga stajala na statusu 200 sa veličinom „(disk
 * cache)", nisu dolazila do servera, i osvežavanje nije pomagalo.
 *
 * Ovde zato stoji i druga brana: poziv koji ne gleda u keš ne može da naleti na
 * zapamćen odgovor, pa niko ne mora da briše keš rukom.
 */
async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    cache: 'no-store'
  });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return response.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return readBody<T>(response);
}

/**
 * Telo odgovora, ili ništa kad ga nema.
 *
 * Poziv koji samo nešto upiše vraća 200 bez tela; slepo čitanje JSON-a je
 * tada padalo sa „Unexpected end of JSON input" i operater je video grešku
 * iako je upis PROŠAO. Zabeleženo 27.08.2026 na upisu zamenika poslovođe.
 */
async function readBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  return (text.length === 0 ? undefined : JSON.parse(text)) as T;
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return readBody<T>(response);
}

async function del<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { method: 'DELETE', credentials: 'include' });
  if (!response.ok) {
    throw new Error(await describeFailure(response, path));
  }
  return readBody<T>(response);
}

/** Poruka mora reći šta je palo, da operater ne gleda prazan ekran bez objašnjenja. */
async function describeFailure(response: Response, path: string): Promise<string> {
  const detail = await response.text().catch(() => '');

  // Sastavljanje poruke stoji u `greska.ts` — oblik tela je dogovor sa serverom, a
  // dogovori se lome nečujno, pa mora biti pokriven testom bez mreže.
  return opisiGresku(response.status, detail, path);
}

export const api = {
  // Čekanje (max_wait_days) čita server iz podešavanja; ekran ga više ne šalje (B3).
  awaiting: (_maxWaitDays?: number) =>
    get<AwaitingItem[]>('/api/transport/awaiting'),

  reconciliation: () => get<Reconciliation>('/api/transport/awaiting/reconciliation'),

  deliveries: (_maxWaitDays?: number) =>
    get<Delivery[]>('/api/transport/deliveries'),

  vehicles: () => get<Vehicle[]>('/api/planning/vehicles'),

  drivers: (date?: string) =>
    get<Driver[]>(`/api/planning/drivers${date ? `?date=${date}` : ''}`),

  settings: () => get<Settings>('/api/planning/settings'),

  /** Prag popunjenosti ili čekanje. Opseg proverava server (B3). */
  updateSetting: (key: string, value: string) =>
    put<{ key: string; value: string; updatedAt: string }>(
      `/api/planning/settings/${encodeURIComponent(key)}`, { value }),

  /**
   * Pravi vozaču nov link. Stari time prestaje da važi — što je i smisao kad se
   * telefon izgubi ili vozač ode.
   */
  createDriverLink: (id: number) =>
    post<DriverLink>(`/api/planning/drivers/${id}/link`, {}),

  /** Postojeći link vozača, bez pravljenja novog. */
  /**
   * Postojeci link vozaca. Sa `tura` nosi i broj ture — bez njega ekran vozaca
   * sam bira, pa vozac sa dve nezavrsene ture ne moze da bira sa strane.
   */
  driverLink: (id: number, tura?: number) =>
    get<DriverLink>(
      `/api/planning/drivers/${id}/link${tura ? `?tura=${tura}` : ''}`),

  /** Poslovođa javlja da je roba naručena i kad stiže. Ne dira ERP. */
  promiseGoods: (code: string, body: {
    documentKey: string;
    articleCode: string;
    expectedKind: string;
    expectedCode: string;
    expectedOn: string;
    note: string | null;
  }) => post<{ upisano: boolean }>(`/api/store/${code}/expected`, body),

  locations: () => get<NetworkLocation[]>('/api/locations'),

  /** Objekti kojima koordinata nije potvrđena — bez toga ne mogu u turu. */
  pendingLocations: () => get<PendingLocation[]>('/api/locations/pending-verification'),

  confirmLocation: (id: number, latitude: number, longitude: number) =>
    post<{ id: number; isVerified: boolean }>(`/api/locations/${id}/confirm`, { latitude, longitude }),

  /** Šta šifarnik kaže o vozačima, a mi još ne znamo. */
  rosterPlan: () => get<RosterPlan>('/api/planning/drivers/sifarnik'),

  /** Primenjuje samo ono što sme samo; sukobi ostaju netaknuti. */
  applyRoster: () =>
    post<{
      dodato: number;
      osvezeno: number;
      ugaseno: number;
      cekaCoveka: number;
      /** Koga šifarnik gasi, a mi nismo — jer ima otvorenu turu. */
      odlozeno: string[];
    }>('/api/planning/drivers/sifarnik/primeni', {}),

  /** Čovekova odluka o jednom sukobu. */
  resolveRoster: (driverId: number, resolution: ConflictResolution) =>
    post<{ razreseno: boolean }>(
      `/api/planning/drivers/${driverId}/razresi`, { resolution }),

  /**
   * Vraća potvrđenu adresu u red, kad se ispostavi da pin nije dobar.
   *
   * Razlog je obavezan: neko je tačku već potvrdio i potpisao se, pa je ovo
   * ispravka tuđeg zapisa. Ostaje uz adresu, ne u dnevniku.
   */
  unlockAddress: (id: number, reason: string) =>
    post<{ id: number; status: number }>(`/api/addresses/${id}/unlock`, { reason }),

  /** Šta ne može u turu i zašto, skupljeno na jedno mesto. */
  obstacles: () => get<ObstacleGroup[]>('/api/transport/obstacles'),

  /**
   * „Ovo se ne vozi" — otpremnica izlazi sa svih spiskova logistike.
   *
   * ERP se ne dira: otpremnica tamo ostaje otvorena. Razlog je obavezan, pa se
   * odluka posle može i proveriti i opozvati.
   */
  skloniIsporuku: (documentKey: string, reason: string) =>
    post<{ poruka: string }>('/api/transport/obstacles/dismiss', { documentKey, reason }),

  /** Šta je sklonjeno, sa razlogom i imenom. */
  sklonjeneIsporuke: () =>
    get<SklonjenaIsporuka[]>('/api/transport/obstacles/dismissed'),

  /** Vraćanje sklonjene otpremnice na spiskove. */
  vratiIsporuku: (documentKey: string) =>
    post<{ poruka: string }>('/api/transport/obstacles/restore', { documentKey, reason: null }),

  /**
   * Potvrda da je roba od dobavljaca stigla, iako kalkulacija jos nije usla.
   * Vazi samo za oznake 253/254; rezervacije nema dok ERP ne vidi zalihu.
   */
  robaJeStigla: (documentKey: string, note?: string) =>
    post<{ upisano?: boolean; vec?: boolean }>(
      `/api/transport/isporuke/${encodeURIComponent(documentKey)}/roba-je-stigla`,
      { note: note ?? null }),

  /**
   * Isto, ali sa POSLOVODJINOG ekrana.
   *
   * `/api/transport` je 21.09.2026. zatvoren za rolu operatera, pa je poslovodjin
   * klik od tada vracao 403 — dugme je izgledalo ispravno i nije radilo nista.
   * Njegova ruta ide kroz objekat, gde se proverava da sme da radi sa tim
   * objektom i da je otpremnica bas njegova.
   */
  robaJeStiglaZaObjekat: (code: string, documentKey: string, note?: string) =>
    post<{ upisano?: boolean; vec?: boolean }>(
      `/api/store/${encodeURIComponent(code)}`
      + `/isporuke/${encodeURIComponent(documentKey)}/roba-je-stigla`,
      { note: note ?? null }),

  /** Povlacenje potvrde — roba ipak nije tu, ili je pustena greskom. */
  povuciRobaJeStigla: (documentKey: string) =>
    del<{ povuceno: boolean }>(
      `/api/transport/isporuke/${encodeURIComponent(documentKey)}/roba-je-stigla`),

  /**
   * „Kupac pristaje — vozi bez ove robe."
   *
   * Skida sa tovara tacno ono cega nema nigde. Otpremnica u ERP-u ostaje cela i
   * neisporucena, pa se ostatak ne gubi.
   */
  voziBezOstatka: (documentKey: string, note?: string) =>
    post<{ upisano: number; ostavljeno: string[]; potvrdio: string }>(
      `/api/transport/isporuke/${encodeURIComponent(documentKey)}/vozi-bez-ostatka`,
      { note: note ?? null }),

  /** Povlacenje te odluke — otpremnica se opet vozi cela. */
  povuciVoziBezOstatka: (documentKey: string) =>
    del<{ povuceno: number }>(
      `/api/transport/isporuke/${encodeURIComponent(documentKey)}/vozi-bez-ostatka`),

  /** Javlja objektima da pošalju u magacin robu koju čeka kupac. */
  askStoresToSendGoods: () =>
    post<{ trazeno: number; javljeno: number; objekti: string[] }>(
      '/api/transport/dovlacenja/javi', {}),

  /** Nalozi kojima ERP nije rekao gde idu. */
  unresolvedDocuments: () =>
    get<UnresolvedDocument[]>('/api/transport/awaiting/unresolved'),

  /** Operaterov upis odredišta. Ide samo u našu bazu, ne u ERP. */
  recordDestination: (body: {
    sourceDocument: string;
    destinationKind: string;
    destinationCode: string;
    rawText: string | null;
    note: string | null;
    learnPhrase: boolean;
  }) => post<{ upisano: boolean }>('/api/transport/awaiting/destination', body),

  fillEstimate: (vehicleId: number, items: { articleCode: string; quantity: number }[]) =>
    post<FillEstimate>('/api/planning/fill-estimate', { vehicleId, items }),

  templates: (date?: string) =>
    get<RouteTemplate[]>(`/api/planning/templates${date ? `?date=${date}` : ''}`),

  proposal: (templateId: number, date?: string, polaziste?: string) => {
    const upit = new URLSearchParams();
    if (date) upit.set('date', date);
    // Prazno znaci: sa polazista samog pravca. Put pravca se ne menja.
    if (polaziste) upit.set('polaziste', polaziste);

    const rep = upit.toString();
    return get<TourProposal>(
      `/api/planning/templates/${templateId}/proposal${rep ? `?${rep}` : ''}`);
  },

  /**
   * Stvarni put kroz zadate tačke. Traži se kad operater izmeni predlog —
   * linija i kilometraža iz predloga tad govore o turi koja više ne postoji.
   */
  road: (points: { latitude: number; longitude: number; label?: string }[]) =>
    post<RoadRoute>('/api/planning/road', { points }),

  previewTour: (draft: unknown) => post<TourPreview>('/api/planning/tour/preview', draft),

  /** Predlog boljeg redosleda za već složenu turu. Ne dodaje i ne izbacuje stanice. */
  suggestOrder: (draft: unknown) =>
    post<{
      changed: boolean;
      order?: number[];
      beforeKm?: number;
      afterKm?: number;
      savedKm?: number;
      measured?: boolean;
    }>('/api/planning/tour/order', draft),

  saveTour: (draft: unknown) => post<SavedTour>('/api/planning/tour', draft),

  /**
   * Predlozi ispravke naziva ulice. Zove se tek kad operater otvori adresu —
   * savetnik se plaća po pozivu, a predlog vredi samo dok neko gleda u ekran.
   */
  suggestAddress: (id: number) =>
    post<AddressSuggestionResult>(`/api/addresses/${id}/suggest`, {}),

  addressQueue: (limit = 100) => get<PendingAddress[]>(`/api/addresses/queue?limit=${limit}`),

  addressSummary: () => get<AddressQueueSummary>('/api/addresses/queue/summary'),

  /** Adrese koje je poslovođa odbio. Isporuka po njima stoji dok se ne sredi. */
  rejectedAddresses: () => get<RejectedAddress[]>('/api/addresses/rejected'),

  runGeocoding: (max = 50, retry = false) =>
    post<GeocodeRun>(`/api/addresses/geocode?max=${max}&retry=${retry}`, {}),

  confirmAddress: (id: number, body: unknown) =>
    post<{ id: number }>(`/api/addresses/${id}/confirm`, body),

  rejectAddress: (id: number, reason: string, rejectedBy: string) =>
    post<{ id: number }>(`/api/addresses/${id}/reject`, { reason, rejectedBy }),

  tourProgress: (date?: string) =>
    get<TourProgress[]>(`/api/monitor/tours${date ? `?date=${date}` : ''}`),

  /** Stanice jedne ture. Traži se tek kad operater otvori turu. */
  tourDetail: (tourId: number) => get<TourDetail>(`/api/monitor/tours/${tourId}`),

  /** Odbacuje turu koja se neće voziti — roba se time oslobađa. */
  /** Operater zatvara turu koju vozač nije zatvorio. */
  closeTour: (tourId: number, reason: string) =>
    post<void>(`/api/monitor/tours/${tourId}/close`, { reason }),

  abandonTour: (tourId: number, reason: string) =>
    post<{ odbaceno: boolean }>(`/api/monitor/tours/${tourId}/abandon`, { reason }),

  /**
   * Operater sklanja javljanje o kvaru. Zapis o kvaru OSTAJE — iz njega se posle
   * vidi koliko tura stane i kojim vozilom.
   */
  resolveBreakdown: (tourId: number, resolution: string) =>
    post<{ breakdownResolution: string; breakdownClearedAt: string }>(
      `/api/monitor/tours/${tourId}/kvar/reseno`, { resolution }),

  /** Nalog za pakovanje: šta se tovari i kojim redom. */
  packingList: (tourId: number) => get<PackingList>(`/api/monitor/tours/${tourId}/packing`),

  /** Šta bi značilo dodati tačku turi u toku. Ništa ne menja. */
  amendmentCheck: (tourId: number, body: { documentKey: string; position?: number }) =>
    post<AmendmentPlan>(`/api/monitor/tours/${tourId}/amendments/check`, body),

  /** Skida stanicu sa ture u toku. Razlog je obavezan. */
  removeStop: (tourId: number, stopId: number, reason: string) =>
    post<{ id: number; version: number; stanica: string; kupacNajavljen?: boolean }>(
      `/api/monitor/tours/${tourId}/stops/${stopId}/skini`, { reason }),

  /** Operater je vozaču javio telefonom da je tura izmenjena. */
  markAmendmentToldByPhone: (amendmentId: number) =>
    post<{ zabelezeno: boolean }>(`/api/monitor/amendments/${amendmentId}/javljeno`, {}),

  /** Dodaje tačku turi u toku. Razlog je obavezan. */
  addAmendment: (
    tourId: number,
    body: { documentKey: string; position?: number; reason: string }
  ) => post<{ id: number; version: number; position: number; stanica: string }>(
    `/api/monitor/tours/${tourId}/amendments`, body),

  /** Planirano protiv ostvarenog za prozor od N dana. */
  /** Prva noga: je li napravljen prenos u magacin 012 posle mejla. */
  reservationLegs: () => get<ReservationLeg[]>('/api/monitor/prenosi-012'),

  /** Druga noga: roba koja stoji u 012 i nigde ne ide. */
  parkedGoods: () => get<ParkedGoodsView>('/api/monitor/zaglavljeno-012'),

  /**
   * „Kupac odneo sam" — posle prodaje.
   *
   * ERP-ova šifra 252 znači isto, ali se posle zaključenja računa ne može
   * promeniti, a kupac se predomisli posle. Stavka izlazi iz prevoza, a prenos
   * iz magacina rezervacija na objekat koji je prodao se upisuje NEPROKNJIŽEN.
   */
  odneoSam: (
    code: string,
    body: {
      documentKey: string;
      articleCode: string;
      articleName: string;
      quantity: number;
      soldByCode: string;
      note?: string;
    }
  ) => post<CustomerPickupResult>(`/api/store/${code}/odneo-sam`, body),

  /** Šta je kog dana isporučeno, najnoviji dan prvi. */
  delivered: (days: number) => get<DeliveredDay[]>(`/api/monitor/isporuceno?days=${days}`),

  analytics: (days: number) => get<AnalyticsReport>(`/api/monitor/analytics?days=${days}`),

  /** Koliko prodje od prodaje do isporuke. */
  leadTime: (days: number) => get<LeadTimeReport>(`/api/monitor/brzina?days=${days}`),

  /** Koliko dan vozila traje naspram zabelezene ture. */
  vehicleDay: (days: number) => get<VehicleDayReport>(`/api/monitor/dan-vozila?days=${days}`),

  /** Ocene kupaca po vozaču (zahtev vlasnika, 16.10.2026). */
  customerRatings: (days: number) =>
    get<CustomerRatingReport>(`/api/monitor/ocene?days=${days}`),

  /** Preracunava kad su vozila izasla i vratila se, za skorije ture. */
  refreshVehicleWindows: (days: number) =>
    post<{ considered: number; measured: number; withoutGps: number }>(
      `/api/monitor/prozor-vozila?days=${days}`, {}),

  /** Moguće odluke o robi koja je prenoćila u vozilu. */
  leftoverDecisions: () =>
    get<ExceptionDecisionOption[]>('/api/monitor/leftover/decisions'),

  /** Šta je urađeno sa robom iz vozila. Ne dira ERP — beleži se šta je fizički urađeno. */
  decideOnLeftover: (tourId: number, decision: string, note: string | null) =>
    post<{ zabelezeno: boolean }>(`/api/monitor/tours/${tourId}/leftover`, { decision, note }),

  openExceptions: () => get<OpenException[]>('/api/monitor/exceptions'),

  exceptionDecisions: () => get<ExceptionDecisionOption[]>('/api/monitor/exceptions/decisions'),

  resolveException: (stopId: number, decision: string, note: string | null, resolvedBy: string) =>
    post<{ stopId: number }>(`/api/monitor/exceptions/${stopId}/resolve`, { decision, note, resolvedBy }),

  goodsInVehicles: () => get<GoodsInVehicle[]>('/api/monitor/goods-in-vehicles'),

  /** Skinute stanice na kojima je vozač ipak bio. */
  deliveriesOnRemovedStops: () =>
    get<DeliveryOnRemovedStop[]>('/api/monitor/skinuto-a-predato'),

  /** Otpremnice zatvorene u ERP-u, a roba im je još na turi i nije predata. */
  closedInErpOnTours: () =>
    get<ClosedInErpOnTour[]>('/api/monitor/zatvoreno-u-erpu'),

  /** Vraća skinutu stanicu na turu. Razlog je obavezan. */
  restoreStop: (tourId: number, stopId: number, reason: string) =>
    post<{ id: number; version: number; stanica: string }>(
      `/api/monitor/tours/${tourId}/stops/${stopId}/vrati`, { reason }),

  /**
   * Online otpremnice o kojima se ne može odlučiti ko vozi. Operater je tu
   * jedini čovek — online nema poslovođu.
   */
  onlineNeodlucene: () =>
    get<OnlineNeodlucena[]>('/api/monitor/online-nije-odluceno'),

  /**
   * Upisi u ERP koji nisu prošli. Spisak je postojao od početka, a do
   * 09.10.2026. ga nijedan ekran nije zvao — vidi `ErpWriteNeedingAttention`.
   */
  writesNeedingAttention: () =>
    get<ErpWriteNeedingAttention[]>('/api/erp/writes/attention'),

  /** Nalozi u ERP-u koje čovek treba da proknjiži. Aplikacija knjiženje ne dira. */
  postings: () => get<PostingToDo[]>('/api/erp/postings'),

  /** Sta aplikacija sme u ERP-u. Samo operater. */
  erpCapabilities: () => get<ErpCapabilities>('/api/erp/mogucnosti'),

  /**
   * Upisuje dimenzije vozila i zapremine kategorija kojih jos nema.
   * Ono sto je covek izmerio ili potvrdio se ne dira.
   */
  seedMeasurements: () => post<MeasurementSeedResult>('/api/planning/mere/seed', {}),

  /** Vozni park iz registra vozila (B7). */
  syncVehicles: () => post<{
    novih: number; osvezenih: number; aktivnihTeretnih: number; saZapreminom: number; promene: string[];
  }>('/api/planning/vozila/iz-registra', {}),

  /**
   * Poruke kupcima i vozačima: današnje, sve što još čeka, i sve što nije
   * prošlo a niko ga nije sklonio.
   */
  messages: () => get<OutboxMessage[]>('/api/monitor/messages'),

  /** Ide li red poruka, po kanalu. Prikazuje se samo kad nešto stoji. */
  outboxHealth: () => get<ChannelHealth[]>('/api/monitor/messages/zdravlje'),

  /** Pozadinski poslovi i da li rade (K22). */
  backgroundJobs: () => get<PozadinskiPosao[]>('/api/monitor/pozadina'),

  /** Rezervacije za kupce (008): alarmi i šta stoji na nalozima — svi objekti. */
  rezervacije: () => get<RezervacijePregled>('/api/monitor/rezervacije'),

  /** Koliko alarma rezervacija čeka — za brojku na kartici. */
  rezervacijeBroj: () => get<{ otvoreno: number }>('/api/monitor/rezervacije/broj'),

  /** Magacin: „skinuto sa naloga" ili „nije taj kupac". */
  zabeleziRezervaciju: (id: number, radnja: RezervacijaRadnja) =>
    post<void>(`/api/monitor/rezervacije/${id}/${radnja}`, {}),

  /**
   * „Video sam i pozvao ga." Sklanja odbijenicu sa spiska.
   *
   * Nije ponovno slanje: kupcu koji u ERP-u ima samo fiksni broj SMS ne može
   * stići ni iz desetog pokušaja. Jedino što pomaže je telefonski poziv, pa
   * ovaj potez beleži da je obavljen.
   */
  acknowledgeMessage: (id: number) =>
    post<{ id: number; acknowledgedAt: string; acknowledgedBy: string }>(
      `/api/monitor/messages/${id}/acknowledge`, {}),

  /** Stanje Akton naloga. Ne šalje nijednu poruku i ne košta. */
  messagingAccount: () => get<MessagingAccount>('/api/monitor/messages/account'),

  // --- Razvoz koji objekat vozi sam ---------------------------------------

  /** Šta objekat vozi sam, sa preprekom uz svaku stavku. */
  localDeliveries: (code: string) =>
    get<LocalDelivery[]>(`/api/store/${code}/razvoz`),

  /** Radnici iz ERP šifarnika i vozila, sa zapamćenim izborom. */
  storeCrew: (code: string) => get<StoreCrew>(`/api/store/${code}/razvoz/posada`),

  /** Pamti kojim vozilom objekat vozi. Nije trajna veza nego poslednji izbor. */
  setStoreVehicle: (code: string, vehicleId: number) =>
    post<{ vehicleId: number }>(`/api/store/${code}/razvoz/vozilo`, { vehicleId }),

  /** Slaže turu i vraća putanju od koje se crta QR. */
  createStoreTour: (code: string, body: {
    name?: string;
    vehicleId: number;
    workerUserId: string;
    documentKeys: string[];
  }) => post<{
    id: number; name: string; stops: number;
    driver: string; vehicle: string; path: string;
  }>(`/api/store/${code}/razvoz/tura`, body),

  /** Današnje ture objekta. */
  storeTours: (code: string) => get<StoreTour[]>(`/api/store/${code}/razvoz/ture`),

  /**
   * „Predato je, vozač nije štiklirao."
   *
   * Bez ovoga se roba sa neštiklirane stanice po zatvaranju ture vraća u
   * planiranje, pa kupac dobija isto dvaput. Razlog je obavezan.
   */
  /**
   * Ispravka pogrešnog štrika posle zatvaranja ture: stavka nije predata.
   * Razlog je obavezan; ako je za stavku već upisano u ERP, server odbija i
   * kaže šta tamo treba uraditi.
   */
  undoDelivery: (tourId: number, stopId: number, itemId: number, note: string) =>
    post<{ itemId: number }>(
      `/api/monitor/tours/${tourId}/stops/${stopId}/items/${itemId}/nije-predato`, { note }),

  confirmDelivered: (tourId: number, stopId: number, note: string) =>
    post<{ stopId: number }>(
      `/api/monitor/tours/${tourId}/stops/${stopId}/predato`, { note }),

  /** Operater prebacuje nalog u razvoz prodavnice. */
  moveToLocalDelivery: (documentKey: string, storeCode: string, note: string) =>
    post<{ documentKey: string; storeCode: string }>(
      `/api/monitor/deliveries/${encodeURIComponent(documentKey)}/lokalni-razvoz`,
      { storeCode, note }),

  /** Ko prima poruke po objektu: poslovođa ili zamenik. */
  storeContacts: () => get<StoreContact[]>('/api/monitor/stores/contacts'),

  /** Gde su vozila sada, po GPS uređajima. */
  vehiclePositions: () => get<VehiclePositions>('/api/monitor/vehicles/positions'),

  /** Gde je vozilo bilo tog dana: zaustavljanja sa trajanjem. */
  vehicleTrace: (vehicleId: number, date?: string) =>
    get<VehicleTrace>(`/api/monitor/vehicles/${vehicleId}/trace${date ? `?date=${date}` : ''}`),

  /** Spaja GPS uređaj sa vozilom; prazna oznaka raskida vezu. */
  setVehicleGps: (vehicleId: number, deviceId: string) =>
    post<void>(`/api/monitor/vehicles/${vehicleId}/gps`, { deviceId }),

  /** Postavlja zamenika; prazan broj vraća poruke poslovođi. */
  setStoreContact: (code: string, body: {
    phone?: string; name?: string; until?: string | null; note?: string;
  }) => post<void>(`/api/monitor/stores/${code}/contact`, body),

  /** Probna poruka na SVOJ broj — jedini test koji ne dira kupce. */
  /** `phone` je broj za viber/sms, a adresa za mail. */
  testMessage: (phone: string, channel: 'viber' | 'sms' | 'mail') =>
    post<{ id: number; destination: string | null; status: string; greska: string | null }>(
      '/api/monitor/messages/test', { phone, channel }),

  /**
   * Beleži da je nalog u ERP-u poništen rukom. Aplikacija to ne zaključuje sama —
   * prolazna greška u čitanju ne sme da izgleda kao brisanje.
   */
  /**
   * Drugu nogu (012 → objekat) je čovek uradio rukom u ERP-u. Prima se samo
   * kad ERP pokazuje da je komad izašao iz 012.
   */
  /** Prenos urađen rukom: druga noga, ili povrat kad je `operation` „povrat rezervacije". */
  settleByHand: (
    documentKey: string, articleCode: string, erpDocument: string | null, operation?: string
  ) =>
    post<{ documentNumber: string | null; message: string }>(
      '/api/erp/writes/zatvoreno-rukom', { documentKey, articleCode, erpDocument, operation }),

  withdrawWrite: (idempotencyKey: string, confirmedBy: string, note: string | null) =>
    post<{ documentNumber: string; message: string }>(
      '/api/erp/writes/withdraw', { idempotencyKey, confirmedBy, note }),

  /** Ko je prijavljen. Frontend ovim počinje — bez odgovora ne prikazuje ekran. */
  me: () => get<SignedInUser>('/api/auth/me'),

  /**
   * Ko je prijavljen — i razlika između „niko" (401, prazno) i „server ne
   * odgovara" (greška). Do 15.10.2026. je svaka greška vodila na prijavu, pa je
   * pad ERP-a svakome izgledao kao istekla sesija (analiza 2.0, B2).
   */
  whoAmI: async (): Promise<SignedInUser | null> => {
    const response = await fetch(`${BASE}/api/auth/me`, { credentials: 'include', cache: 'no-store' });
    if (response.status === 401) return null;
    if (!response.ok) throw new Error(await describeFailure(response, '/api/auth/me'));
    return response.json() as Promise<SignedInUser>;
  },

  login: (username: string, password: string) =>
    post<SignedInUser>('/api/auth/login', { username, password }),

  logout: () => post<{ odjavljen: boolean }>('/api/auth/logout', {}),

  /** Pravci sa mestima kroz koja prolaze. Koordinate su predlog dok se ne potvrde. */
  corridors: () => get<Corridor[]>('/api/corridors'),

  /** Naziv, nedeljni raspored i upotreba pravca. */
  updateCorridor: (id: number, name: string, dayFlags: number, isActive: boolean) =>
    put<{ id: number }>(`/api/corridors/${id}`, { name, dayFlags, isActive }),

  /** Ceo spisak objekata pravca, redom. Šalje se u celini, jer je redosled svojstvo spiska. */
  setCorridorStops: (id: number, stops: { kind: string; code: string }[]) =>
    put<{ id: number }>(`/api/corridors/${id}/stops`, { stops }),

  /** Dodaje mesto na put pravca. Ulazi nepotvrđeno i potvrđuje se na mapi. */
  /** Traži koordinatu mestu koje je nema. Predlog, ne potvrda. */
  suggestPlace: (placeId: number) =>
    post<{
      id: number;
      placeName: string;
      candidates: { latitude: number; longitude: number; displayName: string }[];
      note: string | null;
    }>(`/api/corridors/places/${placeId}/suggest`, {}),

  addCorridorPlace: (id: number, placeName: string, afterSequence: number | null) =>
    post<{ id: number }>(`/api/corridors/${id}/places`, { placeName, afterSequence }),

  removeCorridorPlace: (placeId: number) =>
    del<{ obrisano: boolean }>(`/api/corridors/places/${placeId}`),

  /** Potvrđuje jedno mesto, sa koordinatom kakvu je operater ostavio na mapi. */
  confirmCorridorPlace: (id: number, latitude: number, longitude: number, verifiedBy: string) =>
    post<{ id: number }>(`/api/corridors/places/${id}/confirm`, { latitude, longitude, verifiedBy }),

  /** Potvrđuje celu liniju odjednom — ovde se potvrđuje put, ne pojedinačna tačka. */
  confirmCorridor: (id: number, verifiedBy: string) =>
    post<{ confirmed: number; withoutPoint: string[] }>(
      `/api/corridors/${id}/confirm`, { verifiedBy })
};
